import { Dimensions, Platform } from 'react-native';
import { logInfo } from './logHandling';

let { width, height } = Dimensions.get('window');

let guidelineBaseWidth = 350;
let guidelineBaseHeight = 680;

const starTime = 1 * 60 * 10; // 10 means 10 minutes for each exercise

let scale = (size = 1) => (width / guidelineBaseWidth) * size;
let verticalScale = (size = 1) => (height / guidelineBaseHeight) * size;
let moderateScale = (size = 1, factor = 0.5) =>
  size + (scale(size) - size) * factor;

let utils = {
  //  Shuffle Array
  shuffle: (array: unknown[]) => {
    let currentIndex = array.length,
      temporaryValue,
      randomIndex;

    // While there remain elements to shuffle...
    while (0 !== currentIndex) {
      // Pick a remaining element...
      randomIndex = Math.floor(Math.random() * currentIndex);
      currentIndex -= 1;

      // And swap it with the current element.
      temporaryValue = array[currentIndex];
      array[currentIndex] = array[randomIndex];
      array[randomIndex] = temporaryValue;
    }

    return array;
  },

  /**
   * @param itemSettings the currentSettings being sent from the setting component could be [wordsetExercisesTypes, sentencesetExercisesTypes, textsetExercisesTypes]
   * @param exerciseType type of the exercise to change could be [Sprechen, Lesen, Schreiben, Verstehen]
   * @param setType type of set could be [sentence, word, text]
   * @param setting will check what field of data to check [setting or value ]
   * @param value the sent value that will be compared with in the settings object when matched
   * @param settingValue this is used only for small cases to check some settings that don't follow the flow
   * @returns {boolean|*}
   */
  determineSettings: (
    itemSettings: {
      name: string;
      value: unknown;
      [x: string]: unknown;
    }[],
    exerciseType: string,
    setType: string,
    setting: string,
    value: unknown,
    settingValue = '',
  ) => {
    // itemSettings could be wordSettings, sentenceSettings, textSettings
    // Check the setting exerciseType and then check the value of it to assign true or false on select button
    if (setType === 'word' || setType === 'sentence') {
      let settings = itemSettings.filter((item) => {
        // Mainly values of settings are checked here
        if (
          settingValue === '' &&
          item.name.toLowerCase() === exerciseType.toLowerCase() &&
          item[setting] === value
        ) {
          return item;
          // to check the case where recording duration number
        } else if (
          settingValue === 'recordingDuration' &&
          item.name.toLowerCase() === exerciseType.toLowerCase()
        ) {
          return item;
          // to check values when setting value is set and show like a comment what is being checked
        } else if (
          settingValue &&
          item.name.toLowerCase() === exerciseType.toLowerCase() &&
          item.value === value
        ) {
          return item;
        }
      });

      if (settingValue === 'recordingDuration') {
        return settings?.[0]?.value;
      } else {
        return settings.length > 0;
      }
    } else if (setType === 'text') {
      // for text audio speed settings
      if (!exerciseType) {
        return itemSettings === value;
      } else {
        let settings = itemSettings.filter((settings) => {
          // for text settings is closed or open
          if (
            settingValue === '' &&
            settings.name.toLowerCase() === exerciseType.toLowerCase() &&
            settings[setting] === value
          ) {
            return settings;

            // for text settings is closed, questions gap
          } else if (
            settingValue &&
            settings.name.toLowerCase() === exerciseType.toLowerCase() &&
            settings.value === value
          ) {
            return settings;
          }
        });

        return settings.length > 0;
      }
    }
  },
  scale,
  verticalScale,
  moderateScale,
  starTime,
};

export const hasOwnProperty = (entry: unknown, key: string) => {
  return Object.prototype.hasOwnProperty.call(entry, key);
};

const initConsoleTracker = () => {
  // Hide all console logs in production
  let DEBUG = process.env.NODE_ENV !== 'production';
  if (!DEBUG) {
    if (!window.console) {
      window.console = {} as any;
    }
    let methods = ['log', 'debug', 'warn', 'info'];
    for (let i = 0; i < methods.length; i++) {
      // eslint-disable-next-line no-console
      console[methods[i] as keyof typeof console] = function () {} as any;
    }
  }
};

export const isCapital = (letter: string) => {
  return letter === letter.toUpperCase() && letter !== letter.toLowerCase();
};

const initWebHttpTracker = () => {
  // Force all requests to https on browser
  if (
    process.env.NODE_ENV === 'production' &&
    window.location.protocol !== 'https:' &&
    !(
      window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1'
    )
  ) {
    window.location.replace(
      `https:${window.location.href.substring(window.location.protocol.length)}`,
    );
  }
};

const initWebPerformanceTracker = () => {
  if (window.performance) {
    if (window.performance.navigation.type == 1) {
      logInfo('This page is reloaded');
    } else {
      logInfo('This page is not reloaded');
    }
  }
};

export const initTrackers = (platform: Platform['OS']) => {
  initConsoleTracker();
  if (platform === 'web') {
    initWebHttpTracker();
    initWebPerformanceTracker();
  }
};

export default utils;
